/* eslint-disable no-const-assign */
/* eslint-disable no-unused-vars */
import {makeAutoObservable} from 'mobx';
import {enableStaticRendering, useLocalObservable} from 'mobx-react';
import {runInAction} from 'mobx';
import {makePersistable, hydrateStore} from 'mobx-persist-store';
import {createContext, useContext} from 'react';
import api from './api/api';

enableStaticRendering(typeof window === 'undefined');

let store;
const server = typeof window === 'undefined';

class Store {
  user = (!server && localStorage?.appState && JSON.parse(localStorage?.appState)?.user) || null;
  tokens =
    (!server && localStorage?.appState && JSON.parse(localStorage?.appState)?.tokens) || null;

  roles = [];
  currentLocation = '';
  isVerify = false;

  toolbarType = '';
  toolbarLabel = '';
  configToolbarUserFilter = {notWorking: true, working: true, status: '1'};

  timeConfig = {time_config: '', time_start: '', time_end: ''};

  errorsMessages = [];

  preloaderQ = [];

  fullScreen = false;

  focusedTableFilter = '';

  constructor() {
    // console.log('store::constructor');
    makeAutoObservable(this, {}, {autoBind: true});

    if (!server) {
      makePersistable(this, {
        name: 'appState',
        properties: ['user', 'tokens', 'theme'],
        storage: window.localStorage,
        removeOnExpiration: true,
        // debugMode: true,
      }).then(() => {
        console.log('store::persistable');
      });
    }
  }

  setFullScreen() {
    this.fullScreen = !this.fullScreen;
  }

  setFocusFilter(value) {
    this.focusedTableFilter = value;
  }

  setPreloader(status) {
    if (status) {
      const newPreloaderQ = [...this.preloaderQ];
      newPreloaderQ.push(status);
      runInAction(() => {
        this.preloaderQ = newPreloaderQ;
      });
    } else {
      const timeout = setTimeout(() => {
        const newPreloaderQ = [...this.preloaderQ];
        newPreloaderQ.pop();
        runInAction(() => {
          this.preloaderQ = newPreloaderQ;
        });

        return () => clearTimeout(timeout);
      }, 400);
    }
  }

  filterData(data) {
    if (!data?.success) {
      const shareErrors = Object.entries(data?.data);
      const errorsArr = shareErrors.reduce((sum, current) => {
        return [...sum, ...current[1]];
      }, []);

      this.errorsMessages = errorsArr;
      return null;
    } else if (data?.success === 'canceled') {
      return null;
    } else if (!!data?.success && typeof data?.success !== 'string') {
      return data?.data;
    } else {
      this.errorsMessages = [data.success];
      return null;
    }
  }

  setCurrentLocation(location) {
    this.currentLocation = location;
  }

  setIsVerify(bool) {
    this.isVerify = bool;
  }

  //Auth//////////////////////////

  async signOut() {
    const token = this.user.main_token;
    await api.revokeToken(token, token);
    this.timeConfig = {time_config: ''};
    this.user = null;
    this.tokens = null;
  }

  clearAuthState() {
    this.user = null;
    this.tokens = null;
    this.roles = [];
    this.isVerify = false;
    this.errorsMessages = [];

    if (server || !window.localStorage) {
      return;
    }

    const persistedState = window.localStorage.getItem('appState');

    if (!persistedState) {
      return;
    }

    try {
      const nextState = JSON.parse(persistedState);
      delete nextState.user;
      delete nextState.tokens;
      window.localStorage.setItem('appState', JSON.stringify(nextState));
    } catch (error) {
      window.localStorage.removeItem('appState');
    }
  }

  clearUserAuthState() {
    this.user = null;
    this.roles = [];
    this.isVerify = false;
    this.errorsMessages = [];

    if (server || !window.localStorage) {
      return;
    }

    const persistedState = window.localStorage.getItem('appState');

    if (!persistedState) {
      return;
    }

    try {
      const nextState = JSON.parse(persistedState);
      delete nextState.user;
      window.localStorage.setItem('appState', JSON.stringify(nextState));
    } catch (error) {
      window.localStorage.removeItem('appState');
    }
  }

  clearTemporaryToken() {
    this.tokens = null;

    if (server || !window.localStorage) {
      return;
    }

    const persistedState = window.localStorage.getItem('appState');

    if (!persistedState) {
      return;
    }

    try {
      const nextState = JSON.parse(persistedState);
      delete nextState.tokens;
      window.localStorage.setItem('appState', JSON.stringify(nextState));
    } catch (error) {
      window.localStorage.removeItem('appState');
    }
  }

  async getIp() {
    const client = await api.getIp();
    return client?.data || client;
  }

  async getTemporaryToken(ip, locale, userAgent) {
    if (this.user?.main_token) {
      return {
        success: false,
        code: 'ALREADY_AUTHENTICATED',
        message: 'User is already authenticated',
      };
    }

    this.user = null;
    this.tokens = null;
    this.user = {user_ip: ip, user_locale: locale, user_agent: userAgent};

    const token = await api.getTemporaryToken({ip, locale, userAgent});

    if (token?.success) {
      this.setTemporaryTokenData(token.data);
    }

    this.user = {...this.user};

    return token;
  }

  setTemporaryTokenData(data) {
    if (!data?.temp_token) {
      return;
    }

    const numericCreatedAt = Number(data.created_at);
    const createdAtMs = Number.isFinite(numericCreatedAt)
      ? numericCreatedAt > 1000000000000
        ? numericCreatedAt
        : numericCreatedAt * 1000
      : Date.parse(data.created_at);

    this.tokens = {
      authToken: data.auth_token,
      tempToken: data.temp_token,
      createdAt: createdAtMs || Date.now(),
    };
  }

  async checkTemporaryToken(token) {
    return api.checkTemporaryToken(token);
  }

  async telegramAuth({idToken, telegramUser, nonce, ip, locale, userAgent}) {
    this.user = {user_ip: ip, user_locale: locale, user_agent: userAgent};
    this.tokens = null;

    const response = await api.telegramAuth({idToken, telegramUser, nonce, ip, locale, userAgent});

    if (!response?.success) {
      return response;
    }

    const mainToken = response?.data?.main_token;

    if (!mainToken) {
      return {
        ...response,
        success: false,
        message: response?.message || 'Telegram authorization failed.',
      };
    }

    this.setMainToken(mainToken);

    return response;
  }

  setMainToken(token) {
    this.tokens = null;
    this.user = {...this.user, main_token: token};
  }

  async verifyAuth({ip, locale, userAgent}) {
    const {main_token, user_ip, user_locale, user_agent} = this.user;
    const user = await api.verifyAuth({
      main_token,
      user_ip: ip,
      user_locale: locale,
      user_agent: userAgent,
    });

    if (user?.authFailed || user?.networkError || user?.serviceUnavailable) {
      return user;
    }

    if (!user) {
      return null;
    }

    runInAction(() => {
      this.user = {
        ...this.user,
        ...user,
        // auth: {
        //   roles: [
        //     // {departament_id: 1, id: 9, level: 1, name: 'Manager LTC'},
        //     // {departament_id: 1, id: 33, level: 1, name: 'ManagerDeputyLTC'},
        //     // {departament_id: 1, id: 16, level: 1, name: 'TeamLeader LTC'},
        //     // {departament_id: 1, id: 15, level: 1, name: 'Manager RD'},
        //     // {departament_id: 1, id: 34, level: 1, name: 'ManagerDeputyRD'},
        //     // {departament_id: 1, id: 17, level: 1, name: 'TeamLeader RD'},
        //     // {departament_id: 1, id: 8, level: 1, name: 'Head of Sales'},
        //     // {departament_id: 1, id: 11, level: 1, name: 'Head of Influence'},
        //     // {departament_id: 1, id: 10, level: 1, name: 'Head of Facebook'},
        //     // {departament_id: 1, id: 6, level: 1, name: 'Facebook Bayer (Profit)'},
        //     // {departament_id: 1, id: 19, level: 1, name: 'Influence Bayer (Cost)'},
        //     // {departament_id: 1, id: 13, level: 1, name: 'Head of Finance'},
        //     // {departament_id: 1, id: 32, level: 1, name: 'Assistant of Finance'},
        //     // {departament_id: 1, id: 26, level: 1, name: 'Hr'},
        //     // {departament_id: 1, id: 28, level: 1, name: 'Manager Support'},
        //     // {departament_id: 1, id: 29, level: 1, name: 'Manager Cold'},
        //     // {departament_id: 1, id: 30, level: 1, name: 'Head Assistant Of Sales'},
        //     // {departament_id: 1, id: 31, level: 1, name: 'CRM Backend'},
        //     // {departament_id: 1, id: 36, level: 1, name: 'TeamLead Influence Bayer'},
        //     // {departament_id: 1, id: 38, level: 1, name: 'CBDO'},
        //     // {departament_id: 1, id: 39, level: 1, name: 'Integrator'},
        //   ],
        // },
      };
    });
    return user;
  }

  setRoles(roles) {
    this.roles = !!roles?.length ? [...roles] : [];
  }

  async revokeToken(revoke_token) {
    const token = this.user.main_token;

    const result = await api.revokeToken(token, revoke_token);

    return result;
  }

  async revokeTempToken(token) {
    try {
      const response = await api.client.delete(
        `user/auth/telegram-bot/${encodeURIComponent(token)}`
      );
      return !!response?.data?.success;
    } catch (error) {
      console.error('Error token:', error);
      return false;
    }
  }

  //Auth/////////////////////////

  /**
   * Універсальний GET-запит
   */
  async requestGet(
    endpoint,
    {params = {}, signal, preloader = true, returnFullObj = false} = {},
    withoutToken = false
  ) {
    if (preloader) this.setPreloader(true);
    const token = this.user.main_token;

    try {
      const config = {
        ...(signal && {signal}),
        headers: {
          Token: `${token}`, // токен у заголовку
        },
      };

      if (typeof params === 'string') {
        const connector = endpoint.includes('?') ? '&' : '?';
        if (!withoutToken) {
          endpoint += `${connector}token=${encodeURIComponent(token)}&${params}`;
        } else {
          endpoint += `${connector}${params}`;
        }
      } else if (typeof params === 'object' && params !== null) {
        config.params = withoutToken ? {...params} : {token, ...params};
      } else {
        config.params = withoutToken ? {} : {token};
      }

      const response = await api.client.get(endpoint, config);
      if (returnFullObj) return response.data;
      return this.filterData(response.data);
    } catch (error) {
      if (
        error?.name === 'CanceledError' ||
        error?.code === 'ERR_CANCELED' ||
        error?.name === 'AbortError' ||
        error?.message === 'canceled'
      ) {
        return null;
      }

      console.error(`GET ${endpoint} — помилка`, error);
      this.errorsMessages = [error.message];
      return null;
    } finally {
      if (preloader) this.setPreloader(false);
    }
  }

  isFormData(value) {
    return (
      typeof FormData !== 'undefined' && // перевіряє чи є FormData у середовищі
      value instanceof FormData &&
      typeof value.append === 'function' && // гарантія, що має потрібний інтерфейс
      Object.prototype.toString.call(value) === '[object FormData]'
    );
  }

  /**
   * Універсальний POST-запит
   */
  async requestPost(endpoint, {data = {}, signal, preloader = true} = {}) {
    if (preloader) this.setPreloader(true);
    const token = this.user.main_token;
    const isForm = this.isFormData(data);
    console.log(data, isForm);
    const payload = isForm
      ? (() => {
          data.append('token', token);
          return data;
        })()
      : {token, ...data};

    try {
      const config = {
        ...(signal && {signal}),
        ...(isForm && {headers: {'Content-Type': 'multipart/form-data'}}),
      };
      const response = await api.client.post(endpoint, payload, config);
      return this.filterData(response.data);
    } catch (error) {
      console.error(`POST ${endpoint} — помилка`, error);
      this.errorsMessages = [error.message];
      return null;
    } finally {
      if (preloader) this.setPreloader(false);
    }
  }

  /**
   * Універсальний PATCH-запит
   */
  async requestPatch(endpoint, {data = {}, signal, preloader = true} = {}) {
    if (preloader) this.setPreloader(true);
    const token = this.user.main_token;
    const isForm = this.isFormData(data);
    const payload = isForm
      ? (() => {
          data.append('token', token);
          return data;
        })()
      : {token, ...data};

    try {
      const config = {
        ...(signal && {signal}),
        ...(isForm && {headers: {'Content-Type': 'multipart/form-data'}}),
      };
      const response = await api.client.patch(endpoint, payload, config);
      return this.filterData(response.data);
    } catch (error) {
      console.error(`PATCH ${endpoint} — помилка`, error);
      this.errorsMessages = [error.message];
      return null;
    } finally {
      if (preloader) this.setPreloader(false);
    }
  }

  /**
   * Універсальний DELETE-запит
   */
  async requestDelete(endpoint, {data = {}, signal, preloader = true} = {}) {
    if (preloader) this.setPreloader(true);
    const token = this.user.main_token;

    try {
      const config = {
        data: {token, ...data},
        ...(signal && {signal}),
      };
      const response = await api.client.delete(endpoint, config);
      return this.filterData(response.data);
    } catch (error) {
      console.error(`DELETE ${endpoint} — помилка`, error);
      this.errorsMessages = [error.message];
      return null;
    } finally {
      if (preloader) this.setPreloader(false);
    }
  }

  //timeConfig///////////////////

  setTimeConfig(data) {
    return (this.timeConfig = {...data});
  }

  //timeConfig///////////////////

  //toolbars///////////////////////////
  setToolbarType(type) {
    this.toolbarType = type;
  }

  setToolbarLabel(str) {
    this.toolbarLabel = str;
  }

  setConfigToolbarUserFilter(conf, cleaning = false) {
    if (!cleaning) {
      const newConfig = {...this.configToolbarUserFilter, ...conf};
      return (this.configToolbarUserFilter = newConfig);
    }
    return (this.configToolbarUserFilter = {notWorking: true, working: true, status: '1'});
  }

  //toolbars///////////////////////////

  async hydrate(initialProps) {
    // console.log('store::hydrate')
    Object.keys(initialProps).forEach((key) => {
      // console.log('store::hydrate, adding initial');
      // console.log(key, initialProps[`${key}`]);
      this[`${key}`] = initialProps[`${key}`];
    });
    await hydrateStore(this);
  }
}

const initialize = (initialProps) => {
  const _store = store ?? new Store();
  if (initialProps) {
    (async () => {
      await _store.hydrate(initialProps);
    })();
  }
  return _store;
};

const StoreContext = createContext(null);

const StoreProvider = ({store, children}) => {
  return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>;
};

const initStore = (initialProps) => {
  // console.log('store::initStore');
  // console.log('initialProps', initialProps);
  return initialize(initialProps);
};

const useStore = () => {
  return useContext(StoreContext);
};

export {StoreProvider, initStore, useStore, Store};

// export default new Store();
